/**
 * Browse-state persistence for the library index.
 *
 * Remembers the user's last applied browse state — selected system, sort,
 * view, and the sidebar filter set — across full page loads (e.g. after
 * launching a game or closing the tab). The transient search query is
 * deliberately excluded so a stale search doesn't reappear on every return.
 *
 * Mechanism: a single `state.subscribe` whose selector is the *serialized*
 * browse subset. The store's Object.is dedup (see state.js) means we write to
 * localStorage only when that subset actually changes — unrelated state churn
 * (and search edits, which aren't in the subset) never hit storage.
 *
 * The persisted blob is version-stamped and defensively validated on read:
 * anything missing, corrupt, or stale yields clean defaults rather than
 * throwing.
 */

import { DEFAULT_FILTERS } from './state.js';
import { LISTED_TYPES, DEFAULT_RELEASE_TYPES } from '../../../src/shared/release-type-vocabulary.js';
import { parseReleaseTypesParam } from '../../../src/shared/release-type.js';

export const BROWSE_KEY = 'retroplay-library-browse';
export const BROWSE_VERSION = 1;

const VALID_SORTS = ['title', 'recent', 'favorites', 'rating'];
const VALID_VIEWS = ['grid', 'list'];

/** Keep the closed, ordered selection; an explicit empty array is meaningful. */
export function sanitizeReleaseTypes(value) {
  if (!Array.isArray(value)) return [...DEFAULT_RELEASE_TYPES];
  if (value.length === 0) return [];
  const selected = LISTED_TYPES.filter(type => value.includes(type));
  return selected.length ? selected : [...DEFAULT_RELEASE_TYPES];
}

/** The URL accepts one comma list or bounded repeated parameters. */
export function parseReleaseTypes(values) {
  const parsed = parseReleaseTypesParam(values.length === 1 ? values[0] : values);
  return parsed.types.length ? parsed.types : null;
}

/** undefined means the feature is off; null means no request is allowed. */
export function releaseTypesParam(filters, features) {
  if (features?.library_release_types !== true) return undefined;
  const selected = sanitizeReleaseTypes(filters?.release_types);
  return selected.length ? selected.join(',') : null;
}

export function includedHiddenTypes(selection, hidden) {
  if (!hidden || Number(hidden.total) <= 0 || !hidden.by_type) return [];
  const current = sanitizeReleaseTypes(selection);
  return LISTED_TYPES.filter(type => !current.includes(type) && Number(hidden.by_type[type]) > 0);
}

export function hasNarrowingFilters(search, filters = {}) {
  return !!(search || filters.genre || filters.tag || filters.favorite || filters.mine ||
    filters.played || filters.letter || filters.top100 || filters.filter_id ||
    filters.achievements || filters.scores);
}

/**
 * Keep only the known filter keys (with their stored values) layered over the
 * canonical defaults. Drops unknown keys and internal underscore keys such as
 * `_filter_name` (a render cache the sidebar back-fills from `filter_id`).
 */
function sanitizeFilters(raw) {
  const f = raw && typeof raw === 'object' ? raw : {};
  const out = { ...DEFAULT_FILTERS };
  for (const key of Object.keys(DEFAULT_FILTERS)) {
    if (f[key] !== undefined) out[key] = f[key];
  }
  out.release_types = sanitizeReleaseTypes(f.release_types);
  return out;
}

/**
 * Project the catalog slice into the serializable browse subset. Omits
 * `search` and any internal/unknown filter keys. Bad sort/view values fall
 * back to the app defaults so we never persist garbage.
 */
export function snapshotBrowseState(catalog) {
  const c = catalog || {};
  return {
    version: BROWSE_VERSION,
    system: typeof c.system === 'string' ? c.system : 'all',
    sort: VALID_SORTS.includes(c.sort) ? c.sort : 'title',
    view: VALID_VIEWS.includes(c.view) ? c.view : 'grid',
    filters: sanitizeFilters(c.filters),
  };
}

/**
 * Read + validate the persisted browse state. Returns a partial
 * { system?, sort?, view?, filters? } suitable for merging into catalog, or
 * null when there's nothing usable. Never throws.
 */
export function readBrowseState(storage = localStorage) {
  let raw;
  try {
    raw = storage.getItem(BROWSE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object' || data.version !== BROWSE_VERSION) {
    return null;
  }

  const out = {};
  if (typeof data.system === 'string') out.system = data.system;
  if (VALID_SORTS.includes(data.sort)) out.sort = data.sort;
  if (VALID_VIEWS.includes(data.view)) out.view = data.view;
  if (data.filters && typeof data.filters === 'object') {
    out.filters = sanitizeFilters(data.filters);
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Restore the persisted browse state into the store on boot. Demotes a
 * `system` that is no longer enabled (e.g. its flag was turned off) to 'all'
 * so the grid doesn't silently filter to a hidden console. Returns the applied
 * subset, or null when there was nothing to restore.
 *
 * `enabledSystemIds` is the list of currently-enabled library system ids
 * (the caller computes it from feature flags). Pass null/undefined to skip the
 * enabled-system check.
 */
export function applyBrowseState(state, enabledSystemIds, storage = localStorage) {
  const browse = readBrowseState(storage);
  if (!browse) return null;

  if (
    browse.system && browse.system !== 'all' &&
    Array.isArray(enabledSystemIds) && !enabledSystemIds.includes(browse.system)
  ) {
    browse.system = 'all';
  }

  const cat = state.get().catalog;
  state.set({
    catalog: {
      ...cat,
      ...browse,
      filters: { ...cat.filters, ...(browse.filters || {}) },
    },
  });
  return browse;
}

/**
 * Kill-switch hygiene: when library_my_roms is OFF, a previously persisted
 * (or deep-linked) mine facet must not survive as a ghost chip/query — the
 * toggle that would clear it is hidden. Signed-out mine is deliberately
 * KEPT: its empty state is a sign-in funnel, not a dead control. Call
 * after applyBrowseState + applyUrlParams, before persistence installs.
 * Returns true when it cleared anything.
 */
export function clearMineWhenDisabled(state, features) {
  if (features?.library_my_roms !== false) return false;
  const cat = state.get().catalog;
  if (!cat.filters?.mine) return false;
  state.set({ catalog: { ...cat, filters: { ...cat.filters, mine: false } } });
  return true;
}

/**
 * Subscribe to the store and persist the browse subset whenever it changes.
 * Install AFTER restoring state so the just-restored values don't trigger a
 * redundant write (the subscription captures its baseline at subscribe time).
 *
 * Returns an unsubscribe function.
 */
export function installBrowsePersistence(state, storage = localStorage) {
  return state.subscribe(
    (s) => JSON.stringify(snapshotBrowseState(s.catalog)),
    (serialized) => {
      try {
        storage.setItem(BROWSE_KEY, serialized);
      } catch {
        // Storage full or disabled (private mode). Persistence is a nicety —
        // never let it break the app.
      }
    },
  );
}
